import { useEffect, useState } from 'react';
import { Button } from '@accure/ui';
import { ArrowRight, Lightbulb, X } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Link, type LinkTo } from '@/components/ui/link';
import { cls } from '@/utils/cls';

export function PageActions({ children }: React.PropsWithChildren<object>) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleDismiss = () => {
    setIsAnimating(true);
    setTimeout(() => setIsVisible(false), 300);
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  if (!isVisible || isAnimating) {
    return null;
  }

  return (
    <div
      data-testid="details-page-actions"
      className="fixed bottom-6 left-1/2 z-50 w-full max-w-2xl -translate-x-1/2 px-4 transition-all duration-300 ease-in-out"
    >
      <Card className="border-primary-200 bg-primary-50 p-4 text-primary-700 shadow-lg">
        <div className="mb-2 flex items-start justify-between">
          <div className="flex flex-1 items-start space-x-3">
            <Lightbulb className="mt-0.5 size-5 shrink-0 opacity-70" />
            <div className="text-base font-medium">Need more context?</div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="-mr-2 -mt-2 size-8 shrink-0 p-1 text-primary-700 opacity-70 hover:bg-primary-100 hover:text-primary-700 hover:opacity-100"
            onClick={handleDismiss}
            aria-label="Dismiss suggestion"
          >
            <X className="size-4" />
          </Button>
        </div>
        <div className="ml-8 space-y-2">{children}</div>
      </Card>
    </div>
  );
}

export type PageActionItemProps = {};
export function PageActionItem({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cls('flex items-center justify-between', className)}
      {...props}
    />
  );
}

export function PageActionItemDescription({
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className="text-sm" {...props} />;
}

export type PageActionItemButtonProps = {
  to: LinkTo;
  icon: React.ComponentType<{ className?: string }>;
} & React.HTMLAttributes<HTMLButtonElement>;

export function PageActionItemButton({
  to,
  className,
  icon: Icon,
  children,
  ...props
}: PageActionItemButtonProps) {
  return (
    <Link to={to}>
      <Button
        {...props}
        variant="outline"
        size="sm"
        className={cls(
          'w-40 shrink-0 border-primary-300 bg-transparent text-primary-700 hover:bg-primary-100 hover:text-primary-700',
          className,
        )}
      >
        <div className="flex w-full items-center justify-start">
          <Icon className="mr-2 size-4 shrink-0 opacity-70" />
          <span className="flex-1 text-left">{children}</span>
          <ArrowRight className="ml-1 size-3 shrink-0 opacity-70" />
        </div>
      </Button>
    </Link>
  );
}
